import { getLogger } from '@logtape/logtape';

import { trackCardReadOutcome } from '@/lib/checkin-telemetry.ts';

import { CardReadState, CardReaderStep } from '@/models/CardReader.ts';
import { ModalType } from '@/models/Modal.ts';
import { Patient } from '@/models/Patient.ts';
import { PVSProvider } from '@/models/customers/PVSProvider.ts';
import {
    CardReadActionType,
    CardReadStatus,
    DCCardReader,
    FollowUpActionDto,
    ReadCardRequestDto,
} from '@/models/doc-cirrus/DCCardReader.ts';

import { followUpAction, readCard } from '@/services/cardReadService.ts';
import { closeModal } from '@/services/modalService.tsx';
import { getUserById } from '@/services/patientService.ts';

import { useWalkInStore } from '@/stores/walkInStore.ts';

import { openAppointmentScheduler } from '@/utils/modal/appointmentSchedulerUtils.tsx';
import { convertUserToGenericPatient } from '@/utils/patientUtils.ts';

const logger = getLogger(['util', 'card-read']);

export const setCardReadState = (state: CardReadState): void => {
    useWalkInStore.setState({
        card_read_state: state,
    });
};

export const setCardReadStep = (step: CardReaderStep): void => {
    useWalkInStore.setState((state) => ({
        card_read_state: {
            op_id: state.card_read_state?.op_id ?? '',
            patients: state.card_read_state?.patients ?? [],
            possible_actions: state.card_read_state?.possible_actions ?? [],
            step: step,
        },
    }));
};

export const startBooking = (patient: Patient | undefined): void => {
    if (!patient) return;

    const generic_patient = convertUserToGenericPatient(patient, PVSProvider.DOC_CIRRUS);
    if (!generic_patient) return;

    openAppointmentScheduler({
        mode: 'book',
        patient: generic_patient,
    });
    useWalkInStore.getState().resetAllData();
    closeModal(ModalType.WALK_IN);
};

export const triggerCardRead = async (
    selected_item: string,
    card_readers: DCCardReader[],
    customer_id: string,
    value?: string
): Promise<void> => {
    const [tenant_id, workplace_id] = (value ?? selected_item).split('::');

    let reader: DCCardReader | undefined;
    try {
        reader = getCardReader(card_readers, tenant_id, workplace_id);
    } catch (e) {
        logger.warn('Could not resolve the card reader', { error: e });
        return;
    }
    if (!reader) return;

    useWalkInStore.setState({ is_loading: true });

    const request_dto: ReadCardRequestDto = {
        card_reader_id: reader.reader_id,
        tenant_id: reader.tenant.id,
        workplace_id: reader.workplace.id,
        terminal_id: reader.terminals[0].id,
        action: CardReadActionType.READ,
    };

    try {
        const response = await readCard(request_dto, reader.instance_id, customer_id);

        if (!response) {
            trackCardReadOutcome({ outcome: 'failure', reason: 'read_card_no_response' });
            setCardReadStep(CardReaderStep.RETRY);
            return;
        }

        switch (response.status) {
            case CardReadStatus.APPLIED: {
                const pvs_user = await getUserById<Patient>(
                    response.matched_patients[0],
                    reader.instance_id,
                    PVSProvider.DOC_CIRRUS,
                    true
                );
                if (!pvs_user) {
                    trackCardReadOutcome({ outcome: 'failure', reason: 'patient_lookup_failed' });
                    setCardReadStep(CardReaderStep.RETRY);
                    return;
                }

                setCardReadState({
                    op_id: response.op_id,
                    possible_actions: response.possible_actions,
                    patients: [pvs_user],
                    step: CardReaderStep.SUCCESS,
                });
                trackCardReadOutcome({ outcome: 'success' });
                break;
            }
            case CardReadStatus.MERGED: {
                const pvs_user = await getUserById<Patient>(
                    response.matched_patients[0],
                    reader.instance_id,
                    PVSProvider.DOC_CIRRUS,
                    true
                );
                if (!pvs_user) {
                    trackCardReadOutcome({ outcome: 'failure', reason: 'patient_lookup_failed' });
                    setCardReadStep(CardReaderStep.RETRY);
                    return;
                }

                setCardReadState({
                    op_id: response.op_id,
                    possible_actions: response.possible_actions,
                    patients: [pvs_user],
                    step: CardReaderStep.MERGED,
                    diff_fields: response.diff_fields,
                });
                break;
            }
            // TODO: clean this up
            case CardReadStatus.MATCHING: {
                const promises = response.matched_patients.map((patient_id) =>
                    getUserById<Patient>(patient_id, reader.instance_id, PVSProvider.DOC_CIRRUS, true)
                );
                const users = await Promise.all(promises);
                const filtered_users: Patient[] = users.filter((user) => !!user);
                if (filtered_users.length < users.length) {
                    trackCardReadOutcome({ outcome: 'failure', reason: 'patient_lookup_failed' });
                    setCardReadStep(CardReaderStep.RETRY);
                    return;
                }

                setCardReadState({
                    op_id: response.op_id,
                    possible_actions: response.possible_actions,
                    patients: filtered_users,
                    step: CardReaderStep.MATCHING,
                });
                break;
            }
            case CardReadStatus.CANCELLED: {
                trackCardReadOutcome({ outcome: 'abort' });
                setCardReadStep(CardReaderStep.CANCELLED);
                break;
            }
            default: {
                break;
            }
        }
    } catch (error) {
        trackCardReadOutcome({ outcome: 'failure', reason: 'read_card_error' });
        logger.warn('Card read failed', { error: error });
        setCardReadStep(CardReaderStep.RETRY);
    }

    useWalkInStore.setState({ is_loading: false });
};

export const applyCardReadAction = async (
    card_read_state: CardReadState,
    patient: Patient,
    card_read_action: CardReadActionType,
    customer_id: string
): Promise<void> => {
    const op_id = card_read_state?.op_id;
    if (!patient || !patient.user_id || !op_id) {
        return;
    }

    useWalkInStore.setState({ is_loading: true });

    const request_dto: FollowUpActionDto = {
        op_id: op_id,
        action: card_read_action,
    };

    const response = await followUpAction(
        request_dto,
        patient.instance_id,
        customer_id,
        card_read_action === CardReadActionType.MATCHING_SELECT ? patient.user_id : undefined
    );

    if (!response) {
        trackCardReadOutcome({ outcome: 'failure', reason: 'follow_up_no_response' });
        setCardReadStep(CardReaderStep.RETRY);
        useWalkInStore.setState({ is_loading: false });
        return;
    }

    switch (response.status) {
        case CardReadStatus.APPLIED: {
            setCardReadState({
                ...card_read_state,
                patients: [patient],
                step: CardReaderStep.SUCCESS,
            });
            trackCardReadOutcome({ outcome: 'success' });
            break;
        }
        case CardReadStatus.MERGED: {
            setCardReadState({
                ...card_read_state,
                possible_actions: response.possible_actions,
                patients: [patient],
                diff_fields: response.diff_fields,
                step: CardReaderStep.MERGED,
            });
            break;
        }
        case CardReadStatus.CANCELLED: {
            trackCardReadOutcome({ outcome: 'abort' });
            setCardReadStep(CardReaderStep.CANCELLED);
            break;
        }
        default: {
            break;
        }
    }

    useWalkInStore.setState({ is_loading: false });
};

export const getCardReader = (
    card_readers: DCCardReader[],
    tenant_id: string,
    workplace_id: string
): DCCardReader | undefined => {
    const filtered_card_readers: DCCardReader[] = card_readers.filter(
        (cr: DCCardReader) => cr.tenant.id === tenant_id && cr.workplace.id === workplace_id
    );
    if (filtered_card_readers.length === 0) return undefined;

    if (filtered_card_readers.length > 1) {
        throw new Error(`Multiple card readers found for tenant ${tenant_id} and workplace ${workplace_id}`);
    }

    return filtered_card_readers[0];
};
